import React, { memo, ReactNode, useMemo, useState, Fragment } from 'react';
import { R } from '@core/utils/r';
import { UncontrolledTooltip } from 'reactstrap';
import { CommonService } from '@business/services';
const COLORS = ['info', 'primary', 'success', 'dark', 'secondary', 'warning', 'pink'];

export interface AvatarGroupProjectItem {
  id: string;
  title: string;
  isClient: boolean;
}
export interface AvatarGroupProjectProps {
  items: AvatarGroupProjectItem[];
  onClick?(item: AvatarGroupProjectItem, index: number): void;
  sort?: boolean;
  onTooltip?(item: AvatarGroupProjectItem, index: number): ReactNode | string;
  hideTooltip?: boolean;
  baseId?: string;
  overlap?: boolean;
  children?: ReactNode;
  size?: number;
  onRemove?(user: AvatarGroupProjectItem, index: number): void;
  tooltipContainer?: string;
  viewOnly?: boolean;
  showClients?: boolean;
}

export const AvatarGroupProject = memo((props: AvatarGroupProjectProps) => {
  const { onClick, sort, onTooltip, hideTooltip = false, overlap = false, size = 3, onRemove, viewOnly, showClients = false } = props;
  const [activeItem, setActiveItem] = useState(-1);
  const canRemove = useMemo(() => !viewOnly, [viewOnly]);
  const items = useMemo(() => {
    if (sort) {
      return R.orderBy(props.items, ['isClient', 'title'], ['desc', 'asc']);
    }
    return props.items;
  }, [sort, props.items]);

  const clients = useMemo(() => items.filter(i => i.isClient), [items]);
  const users = useMemo(() => items.filter(i => !i.isClient), [items]);

  const baseId = useMemo(() => {
    return props.baseId || R.random(100, 5000).toString();
  }, [props.baseId]);

  return (
    <div className="avatar-group-projects ml-2">
      {clients.length > 0 && showClients && (
        <Fragment>
          <div className="avatar-group-clients">
            {clients.map((item, _index) => {
              const actualIndex = items.findIndex(i => i.id === item.id);
              return (
                <div
                  onClick={() => onClick && onClick(item, actualIndex)}
                  className="avatar-group-item"
                  style={{
                    marginLeft: overlap ? `-${size / 4}rem` : 'initial',
                    zIndex: actualIndex === activeItem ? items.length + 2 : items.length - actualIndex,
                  }}
                  key={'_avatar_' + baseId + item.id}
                >
                  <a className="d-inline-block" id={'user_item' + baseId + item.id}>
                    <div
                      onMouseOver={() => setActiveItem(actualIndex)}
                      onMouseLeave={() => setActiveItem(-1)}
                      className="avatar-md"
                      style={{ width: size + 'rem', height: size + 'rem' }}
                    >
                      {activeItem === actualIndex && !!onRemove && canRemove && (
                        <a className="btn-avatar-close" onClick={() => onRemove(item, actualIndex)}>
                          <i className="fas fa-times text-white font-size-10"></i>
                        </a>
                      )}
                      <span className={`avatar-title rounded-circle bg-secondary text-white ${size == 3 ? 'font-size-16' : 'font-size-12'}`}>
                        {CommonService.getShortNameFromString(item.title)}
                      </span>

                      {!hideTooltip && (
                        <UncontrolledTooltip container={props.tooltipContainer} placement="top" target={'user_item' + baseId + item.id}>
                          {onTooltip ? onTooltip(item, actualIndex) : item.title + (item.isClient ? ' (Client)' : '')}
                        </UncontrolledTooltip>
                      )}
                    </div>
                  </a>
                </div>
              );
            })}
            <span className="avatar-group-clients-label">Clients</span>
          </div>
          <div style={{ width: 10 }}></div>
        </Fragment>
      )}
      <div className="avatar-group-users">
        {(showClients ? users : items).map((item, _index) => {
          const actualIndex = items.findIndex(i => i.id === item.id);
          return (
            <div
              onClick={() => onClick && onClick(item, actualIndex)}
              className="avatar-group-item"
              style={{
                marginLeft: overlap ? `-${size / 4}rem` : 'initial',
                zIndex: actualIndex === activeItem ? items.length + 2 : items.length - actualIndex,
              }}
              key={'_avatar_' + baseId + item.id}
            >
              <a className="d-inline-block" id={'user_item' + baseId + item.id}>
                <div
                  onMouseOver={() => setActiveItem(actualIndex)}
                  onMouseLeave={() => setActiveItem(-1)}
                  className="avatar-md"
                  style={{ width: size + 'rem', height: size + 'rem' }}
                >
                  {activeItem === actualIndex && !!onRemove && canRemove && (
                    <a className="btn-avatar-close" onClick={() => onRemove(item, actualIndex)}>
                      <i className="fas fa-times text-white font-size-10"></i>
                    </a>
                  )}
                  <span
                    className={`avatar-title rounded-circle bg-${item.isClient ? 'secondary' : 'primary'} text-white ${
                      size == 3 ? 'font-size-16' : 'font-size-12'
                    }`}
                  >
                    {CommonService.getShortNameFromString(item.title)}
                  </span>

                  {!hideTooltip && (
                    <UncontrolledTooltip container={props.tooltipContainer} placement="top" target={'user_item' + baseId + item.id}>
                      {onTooltip ? onTooltip(item, actualIndex) : item.title + (item.isClient ? ' (Client)' : '')}
                    </UncontrolledTooltip>
                  )}
                </div>
              </a>
            </div>
          );
        })}
      </div>

      {props.children}
    </div>
  );
});
